import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useFAQs } from "../../hooks/useFAQs";
import CustomPagination from "../../common/Pagination";
import ConfirmationModal from "../../common/ConfirmationModal";
import { TableStatus } from "../../components/TableStatus";
import FAQModal from "./FAQModal";

export default function FAQTablePage() {
    const queryClient = useQueryClient();

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const {
        faqs,
        pagination,
        isLoading,
        isError,
        error,
        deleteFAQ,
        createFAQ,
        updateFAQ,
        isDeleting,
        isCreating,
        isUpdating,
    } = useFAQs(currentPage, itemsPerPage);

    const [faqToDelete, setFaqToDelete] = useState(null);
    const [editingFAQ, setEditingFAQ] = useState(null);

    const [isModalOpen, setIsModalOpen] = useState(false);

    const openCreateModal = () => {
        setEditingFAQ(null);
        setIsModalOpen(true);
    };

    const openEditModal = (faq) => {
        setEditingFAQ(faq);
        setIsModalOpen(true);
    };

    const handleFormSubmit = async (formData) => {
        if (editingFAQ) {
            await updateFAQ({
                id: editingFAQ._id,
                updatedData: formData,
            });
        } else {
            await createFAQ(formData);
        }
        setIsModalOpen(false);
    };

    const handleDelete = async () => {
        if (!faqToDelete) return;

        await deleteFAQ(faqToDelete._id);

        if (faqs.length === 1 && currentPage > 1) {
            setCurrentPage((prev) => prev - 1);
        }

        setFaqToDelete(null);
    };

    return (
        <div className="card shadow-sm">
            <div className="card-header bg-light d-flex justify-content-between align-items-center p-3">
                <h4 className="mb-0 text-primary-emphasis">FAQ Management</h4>
                <div className="d-flex gap-2">
                    <button
                        className="btn btn-primary btn-sm"
                        onClick={openCreateModal}
                    >
                        + Add FAQ
                    </button>
                </div>
            </div>

            <div className="card-body">
                <div className="table-responsive">
                    <table className="table table-hover align-middle">
                        <thead className="table-light">
                            <tr>
                                <th>No</th>
                                <th>Category</th> {/* <-- Added Category Header */}
                                <th>Question</th>
                                <th>Answer</th>
                                <th>Sort</th>
                                <th>Status</th>
                                <th>Created At</th>
                                <th className="text-center">Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            <TableStatus
                                status={isLoading ? "loading" : isError ? "failed" : "succeeded"}
                                error={error}
                                dataLength={faqs.length}
                                colSpan={8} /* <-- Changed from 7 to 8 */
                                loadingText="Loading FAQs..."
                                emptyText="No FAQs found."
                            />

                            {!isLoading && !isError && faqs.map((faq, index) => (
                                <tr key={faq._id}>
                                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>

                                    {/* Added Category Data Cell with text-capitalize */}
                                    <td className="text-capitalize text-muted fw-semibold">
                                        {faq?.category}
                                    </td>

                                    <td>{faq?.question}</td>
                                    <td className="text-truncate" style={{ maxWidth: "300px" }}>
                                        {faq?.answer}
                                    </td>
                                    <td>{faq?.sort}</td>
                                    <td>
                                        {faq?.isActive ? (
                                            <span className="badge bg-success">Active</span>
                                        ) : (
                                            <span className="badge bg-secondary">Inactive</span>
                                        )}
                                    </td>
                                    <td>
                                        {faq?.createdAt
                                            ? new Date(faq.createdAt).toLocaleDateString()
                                            : "N/A"}
                                    </td>
                                    <td className="text-center">
                                        <div className="d-flex justify-content-center gap-2">
                                            <button
                                                className="btn btn-sm btn-outline-primary mr-2"
                                                onClick={() => openEditModal(faq)}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={() => setFaqToDelete(faq)}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {pagination && pagination.totalPages > 1 && (
                <div className="card-footer">
                    <CustomPagination
                        currentPage={currentPage}
                        totalPages={pagination.totalPages}
                        totalItems={pagination.totalRecords}
                        itemsPerPage={itemsPerPage}
                        onPageChange={setCurrentPage}
                    />
                </div>
            )}

            <ConfirmationModal
                show={faqToDelete !== null}
                onClose={() => setFaqToDelete(null)}
                onConfirm={handleDelete}
                title="Confirm Deletion"
                confirmText="Delete"
                isLoading={isDeleting}
                confirmButtonVariant="danger"
            >
                <p className="text-center">Are you sure you want to delete this FAQ?</p>
            </ConfirmationModal>

            <FAQModal
                show={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleFormSubmit}
                initialData={editingFAQ}
                isLoading={isCreating || isUpdating}
            />
        </div>
    );
}